import { useCallback, useMemo } from "react";
import { useAppSelector } from "./redux-hooks";
import { getUserPermissionKeys, TPermissionKey } from "@/utils/permissions";

const useAuthUser = () => {
  const { user } = useAppSelector((s) => s.auth);
  const { roles: rolesCatalog } = useAppSelector((s) => s.roles);

  const userPermissions = useMemo(
    () => getUserPermissionKeys(user, rolesCatalog),
    [user, rolesCatalog]
  );

  const hasPermission = useCallback(
    (required: TPermissionKey | TPermissionKey[]) => {
      // Legacy admins bypass every permission check.
      if (user.isAdmin) return true;
      const requiredPermissions = Array.isArray(required) ? required : [required];
      // Every non-access permission ("module:edit", "module:delete", …) implicitly
      // requires the module's "access" permission. Callers only pass the specific
      // action and the gate check lives here — no more `x:access && x:edit` at call sites.
      return requiredPermissions.some((k) => {
        if (!userPermissions.has(k)) return false;
        const sep = k.indexOf(":");
        if (sep < 0) return true;
        const action = k.slice(sep + 1);
        if (action === "access") return true;
        const mod = k.slice(0, sep);
        return userPermissions.has(`${mod}:access` as TPermissionKey);
      });
    },
    [user.isAdmin, userPermissions]
  );

  return { user, hasPermission };
};

export default useAuthUser;
